import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { Trans } from "react-i18next"
import { AnimatePresence } from "framer-motion"
import { styled, Theme } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import theme from "lib/material-ui/theme"
import { updateViewedUserFollowRequested } from "appRedux/slices/viewedUserSlice"
import { useUpdateCloseFriendMutation } from "appRedux/slices/userApiSlice"
import { CLOSE_FRIENDS, MEDIUM, PENDING } from "constants/index"
import { FOLLOW_REQUEST, FOLLOWING, NOTIFICATIONS_MAP } from "constants/notifications"
import CloseFriendIcon from "components/atoms/_icons/CloseFriendIcon"
import CircularProgress from "components/atoms/CircularProgress"
import FollowButton from "components/molecules/FollowButton"
import FollowRequestButtons from "components/molecules/Notification/FollowRequestButtons"
import FadeInAnimation from "components/animations/FadeInAnimation"
import withPremiumPermission from "components/HOCs/withPremiumPermission"

type ProfileConnectionSectionProps = {
  userId: number;
  handle: string;
  followStatus: FollowStatus;
  requestedToFollow: boolean;
  isCloseFriend: boolean;
}

export default function ProfileConnectionSection(props: ProfileConnectionSectionProps) {
  const { userId, handle, followStatus, requestedToFollow, isCloseFriend } = props

  const [showFollowRequestBanner, setShowFollowRequestBanner] = useState<boolean>(false)

  const [triggerUpdateCloseFriend, updateCloseFriendResult] = useUpdateCloseFriendMutation()

  const dispatch = useDispatch()

  useEffect(() => {
    setShowFollowRequestBanner(!!requestedToFollow)
  }, [requestedToFollow])

  function toggleRequestedToFollow() {
    dispatch(updateViewedUserFollowRequested())
  }

  function handleCloseFriendButtonClicked() {
    triggerUpdateCloseFriend(userId)
  }

  return (
    <ConnectionCtaWrapper>
      <Stack direction="row" alignItems="center" justifyContent="center" gap="8px" width="100%">
        {followStatus === FOLLOWING && <CloseFriendsButtonWithPremiumPermission
            feature={CLOSE_FRIENDS}
            isclosefriend={isCloseFriend && updateCloseFriendResult.status !== PENDING ? 1 : 0}
            onClick={handleCloseFriendButtonClicked}
        >
          {updateCloseFriendResult.status === PENDING ? <CircularProgress/> :
            <CloseFriendIcon
              fill={isCloseFriend ? theme.palette.success[4] : theme.palette.gray[5]}
              stroke={isCloseFriend ? theme.palette.success[4] : theme.palette.gray[12]}
            />
          }
        </CloseFriendsButtonWithPremiumPermission>}
        <FollowButton
          userId={userId}
          handle={handle}
          status={followStatus}
          size={MEDIUM}
        />
      </Stack>
      <AnimatePresence>
        {showFollowRequestBanner && (
          <FadeInAnimation>
            <FollowRequestContainer>
              <div>
                <Trans
                  i18nKey={NOTIFICATIONS_MAP[FOLLOW_REQUEST]?.message}
                  values={{ handle: handle }}
                  count={1}
                  components={{
                    strong: <strong style={{
                      color: theme.palette.gray["15"],
                      fontWeight: 700
                    }}/>
                  }}
                />
              </div>
              <FollowRequestButtons
                followerId={userId}
                onAccept={toggleRequestedToFollow}
                onReject={toggleRequestedToFollow}
              />
            </FollowRequestContainer>
          </FadeInAnimation>
        )}
      </AnimatePresence>
    </ConnectionCtaWrapper>
  )
}

const ConnectionCtaWrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  marginBottom: "8px",

  "& #follow-button": {
    width: "50%"
  }
}))

const FollowRequestContainer = styled(Stack)(({ theme }) => ({
  width: "calc(100% - 32px)",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  margin: "0 16px",
  padding: "8px 12px",
  backgroundColor: theme.palette.gray["3"],
  boxShadow: "0 0 4px 0 rgba(0, 0, 0, 0.08)",
  borderRadius: "4px",
  fontSize: "1.1rem",
  fontWeight: 300
}))

type CloseFriendsButtonProps = {
  theme?: Theme;
  isclosefriend: 0 | 1;
}

const CloseFriendsButton = styled(Stack)(({ theme, isclosefriend }: CloseFriendsButtonProps) => ({
  width: "40px",
  height: "40px",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  backgroundColor: isclosefriend ? theme?.palette?.success["1"] : theme?.palette?.gray["5"],
  borderRadius: "50%",
  cursor: "pointer"
}))

const CloseFriendsButtonWithPremiumPermission = withPremiumPermission(CloseFriendsButton)
